/**
 * Column builders shared by the device and feature pricing config
 * grids' descriptors — the "Overrides" and row-actions columns are
 * structurally identical between the two types (only the disabled/
 * tooltip predicate differs), so each wrapper's `buildColumns` calls
 * these instead of hand-rolling the column twice.
 */
import { MoreVert, Settings } from '@mui/icons-material';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import React from 'react';

/**
 * @param {function} t - i18next translate function
 * @param {function} onOpenOverrides - (row) => void
 * @param {function} getOverridesGate - row => one of:
 *   - { disabled: boolean, tooltip: string|null } — normal overrides button
 *   - { na: true, tooltip: string|null } — plain "N/A" text (device: FLAT pricing)
 *   - { naButton: true, tooltip: string } — disabled button+icon "N/A" (feature: customer-only)
 */
export function buildOverridesColumn({ t, onOpenOverrides, getOverridesGate }) {
  return {
    field: 'overrides',
    headerName: t('billing.pricingConfig.columnOverrides'),
    width: 120,
    renderCell: params => {
      const count = params.row.overrides?.length || 0;
      const gate = getOverridesGate(params.row);

      if (gate.na) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.disabled">
              {t('billing.pricingConfig.na')}
            </Typography>
          </Box>
        );
      }

      if (gate.naButton) {
        const naButton = (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Button
              size="small"
              variant="text"
              color="inherit"
              startIcon={<Settings fontSize="small" />}
              disabled
              sx={{ minWidth: 80 }}
            >
              {t('billing.pricingConfig.na')}
            </Button>
          </Box>
        );
        return gate.tooltip ? (
          <Tooltip title={gate.tooltip}>
            <span>{naButton}</span>
          </Tooltip>
        ) : (
          naButton
        );
      }

      const overridesButton = (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Button
            size="small"
            variant={count > 0 && !gate.disabled ? 'outlined' : 'text'}
            color={count > 0 && !gate.disabled ? 'primary' : 'inherit'}
            startIcon={<Settings fontSize="small" />}
            onClick={e => {
              e.stopPropagation();
              onOpenOverrides(params.row);
            }}
            disabled={gate.disabled}
            sx={{ minWidth: 80, color: gate.disabled ? 'text.disabled' : undefined }}
          >
            {count}
          </Button>
        </Box>
      );

      if (gate.disabled && gate.tooltip) {
        return (
          <Tooltip title={gate.tooltip} arrow>
            {overridesButton}
          </Tooltip>
        );
      }
      return overridesButton;
    },
  };
}

/**
 * @param {function} isRowDisabled - optional row => boolean; device rows can be
 *   disabled by the virtual-billing/inventory-policy gate, feature rows never are.
 */
export function buildActionsColumn({ onActionMenuOpen, isRowDisabled = () => false }) {
  return {
    field: 'actions',
    headerName: '',
    width: 50,
    sortable: false,
    disableColumnMenu: true,
    renderCell: params => {
      const hasDefaultPrice = !!params.row.default_price;

      // Don't show actions for disabled rows or rows without pricing configured
      if (isRowDisabled(params.row) || !hasDefaultPrice) {
        return null;
      }

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <IconButton size="small" onClick={e => onActionMenuOpen(e, params.row)} sx={{ color: 'text.secondary' }}>
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>
      );
    },
  };
}

/**
 * Renders a labelled value cell (edit-mode Select vs. read-only Typography),
 * matching the display pattern used by every enum column in these grids
 * (pricing unit, device type).
 */
export function EnumCellDisplay({ label, disabled = false }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Typography variant="body2" color={disabled ? 'text.disabled' : 'text.primary'}>
        {label}
      </Typography>
    </Box>
  );
}
